import type { ServiceOrder, ServiceWo, ServiceVehicle, ServiceCommissionRow, ScheduleWrite, ScheduleResult } from './types';

export interface ServiceOrdersQuery {
  from: string;
  to: string;
  sid?: number | null;
  /** Technician code (ss-tech) to filter to a single tech. */
  tech?: string;
  /** "all" | "open" | "complete" — by work-order header status. */
  status?: 'all' | 'open' | 'complete';
  /** Which date axis from/to filter on. 'promise' backs the Agenda calendar. */
  dateField?: 'idate' | 'promise';
  limit?: number;
}

/**
 * v2.42 — Service Shop ("Taller") work-order job lines. Optional module:
 * when service_shop is off the route 404s, which we surface as a clear
 * "module disabled" error so the page can explain rather than spin.
 */
export async function fetchServiceOrders(q: ServiceOrdersQuery): Promise<ServiceOrder[]> {
  const params = new URLSearchParams({ from: q.from, to: q.to });
  if (q.sid) params.set('sid', String(q.sid));
  if (q.tech) params.set('tech', q.tech);
  if (q.status && q.status !== 'all') params.set('status', q.status);
  if (q.dateField) params.set('date_field', q.dateField);
  if (q.limit) params.set('limit', String(q.limit));
  const res = await fetch(`/api/service-orders?${params}`, { credentials: 'same-origin' });
  if (!res.ok) {
    if (res.status === 404) throw new Error('404: módulo de taller no habilitado');
    throw new Error(res.status === 502 || res.status === 503
      ? '503: daemon offline'
      : `service-orders failed: ${res.status}`);
  }
  return (await res.json()) as ServiceOrder[];
}

/**
 * v2.44 — one work-order 360 (labor lines + parts + GP totals). cost/GP fields
 * are present only for owner/manager (the Go layer strips them otherwise).
 */
export async function fetchServiceWo(sid: number, tnum: number): Promise<ServiceWo> {
  const res = await fetch(`/api/service-wo?sid=${sid}&tnum=${tnum}`, { credentials: 'same-origin' });
  if (!res.ok) {
    if (res.status === 404) throw new Error('404: módulo de taller no habilitado');
    throw new Error(`service-wo failed: ${res.status}`);
  }
  return (await res.json()) as ServiceWo;
}

/** v2.44 — vehicle 360: equipment master + ss-hist service timeline. */
export async function fetchServiceVehicle(equip: number): Promise<ServiceVehicle> {
  const res = await fetch(`/api/service-vehicle?equip=${equip}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`service-vehicle failed: ${res.status}`);
  return (await res.json()) as ServiceVehicle;
}

export interface ServiceCommissionQuery { from: string; to: string; sid?: number | null; tech?: string; }

/** v2.44 — per-technician commission accrual over a window + ss-tech ledger balances. */
export async function fetchServiceCommission(q: ServiceCommissionQuery): Promise<ServiceCommissionRow[]> {
  const params = new URLSearchParams({ from: q.from, to: q.to });
  if (q.sid) params.set('sid', String(q.sid));
  if (q.tech) params.set('tech', q.tech);
  const res = await fetch(`/api/service-commission?${params}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`service-commission failed: ${res.status}`);
  return (await res.json()) as ServiceCommissionRow[];
}

/* ── /api/service-productivity ("Rendimiento" — service-technician
 * productivity + labor GP analytics). Cost fields (rate_hrs, labor_cost_mxn,
 * labor_gp_mxn, labor_gp_pct) are OMITTED from the JSON entirely without
 * cost:visible, on BOTH each tech row and totals — typed optional here and
 * gated client-side on `!== undefined`. */

export interface ServiceProductivityCategory {
  category: string;
  jobs: number;
  act_hrs: number;
  revenue_mxn: number;
}

export interface ServiceProductivityTech {
  sid: number;
  tech: string;
  tech_name: string;
  jobs: number;
  est_hrs: number;
  act_hrs: number;
  /** est_hrs / act_hrs, precomputed server-side. 1.0 = on estimate; >1 = faster than estimated. */
  efficiency: number;
  revenue_mxn: number;
  /** revenue_mxn / act_hrs — effective labor $/hr. */
  eff_rate_mxn: number;
  accrued_comm_mxn: number;
  workdays: number;
  util_hrs_per_day: number;
  comebacks: number;
  /** Fraction 0..1 — multiply by 100 for a percent. */
  comeback_rate: number;
  /** Owner/Manager only (cost:visible) — omitted entirely otherwise. */
  rate_hrs?: number;
  labor_cost_mxn?: number;
  labor_gp_mxn?: number;
  /** Fraction 0..1. Owner/Manager only. */
  labor_gp_pct?: number;
  categories: ServiceProductivityCategory[];
}

export interface ServiceProductivityTotals {
  techs: number;
  jobs: number;
  est_hrs: number;
  act_hrs: number;
  efficiency: number;
  revenue_mxn: number;
  labor_cost_mxn?: number;
  labor_gp_mxn?: number;
  labor_gp_pct?: number;
  comebacks: number;
  comeback_rate: number;
}

export interface ServiceProductivity {
  from: string;
  to: string;
  sid: number;
  /** Comeback-detection window (days) used by the heuristic — surfaced so the
   *  UI can render an accurate disclaimer. */
  comeback_days: number;
  totals: ServiceProductivityTotals;
  techs: ServiceProductivityTech[];
}

export interface ServiceProductivityQuery { from: string; to: string; sid?: number | null; }

/**
 * Rendimiento — per-technician productivity + labor GP over a window (jobs,
 * est/act hours, efficiency, effective $/hr, utilization, heuristic comeback
 * rate, and per-category revenue mix). Optional module: 404s the same way
 * fetchServiceOrders does when service_shop is off, so the page can show the
 * same "module not enabled" state.
 */
export async function fetchServiceProductivity(q: ServiceProductivityQuery): Promise<ServiceProductivity> {
  const params = new URLSearchParams({ from: q.from, to: q.to });
  if (q.sid) params.set('sid', String(q.sid));
  const res = await fetch(`/api/service-productivity?${params}`, { credentials: 'same-origin' });
  if (!res.ok) {
    if (res.status === 404) throw new Error('404: módulo de taller no habilitado');
    throw new Error(`service-productivity failed: ${res.status}`);
  }
  return (await res.json()) as ServiceProductivity;
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * v2.62 — Phase-2 gated schedule write. Sets a WO's appointment (status,
 * promise date/time, header tech). Mirrors the CSRF idiom in api/commitDrift.ts:
 * echo the `pacebi_csrf` cookie via `X-CSRF-Token`, mint a fresh idempotency_key
 * per call. Ships behind writes_enabled + service:schedule; the route 404s when
 * writes are disabled.
 */
export async function setWoSchedule(
  w: Omit<ScheduleWrite, 'idempotency_key'>,
): Promise<ScheduleResult> {
  const csrf = readCookie('pacebi_csrf');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch('/api/service/schedule', {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({ ...w, idempotency_key: crypto.randomUUID() }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as ScheduleResult;
}
